import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Select,
  MenuItem,
  Button,
  Grid,
  LinearProgress,
  Checkbox,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import ExcludeEntrevistaButton from "./ExcludeEntrevistaButton";
import { getEntrevistas } from "../api/entrevistaApi";
import { GRUPOS_DICT } from "../model/entrevistaConstants";
import { isAdminSession } from "../../../shared/auth/session";

const SORT_OPTIONS = [
  { value: 'fecha_desc', label: 'Más reciente', model: [{ field: 'fecha', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

function EntrevistasTable() {
  const [entrevistas, setEntrevistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActividades, setFilterActividades] = useState([]);
  const [actividadesDisponibles, setActividadesDisponibles] = useState([]);
  const [filterGrupo, setFilterGrupo] = useState("");
  const [sortModel, setSortModel] = useState([{ field: 'fecha', sort: 'desc' }]);

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'fecha_desc';

  const navigate = useNavigate();
  const isAdmin = useMemo(() => isAdminSession(), []);

  const fetchEntrevistas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEntrevistas();

      // Ahora data ya incluye los datos básicos del paciente
      const formattedData = data.map((entrevista) => ({
        id: entrevista.codigo,
        pacienteId: entrevista.paciente?.id,
        codHistorico: entrevista.paciente?.codHistorico,
        paciente: `${entrevista.paciente?.firstname || ""} ${entrevista.paciente?.lastname || ""}`,
        fechaRaw: entrevista.createdAt,
        fecha: new Date(entrevista.createdAt).toLocaleDateString("es-ES"),
        tipo: entrevista.tipoEntrevista || "",
        grupo: entrevista.paciente?.grupo ?? 0,
        grupoNombre: GRUPOS_DICT[entrevista.paciente?.grupo] || "Otros",
        actividades: entrevista.actividades ? entrevista.actividades.split(", ").map((a) => a.trim()) : [],
      }));

      // Extraer actividades únicas
      const allActividades = new Set();
      formattedData.forEach((item) => item.actividades.forEach((actividad) => allActividades.add(actividad)));

      setActividadesDisponibles([...allActividades]);
      setEntrevistas(formattedData);
    } catch (error) {
      console.error("❌ Error fetching entrevistas:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntrevistas();
  }, [fetchEntrevistas]);

  const filteredEntrevistas = entrevistas.filter((entrevista) => {
    const matchesActividades =
      filterActividades.length === 0 || entrevista.actividades.some((act) => filterActividades.includes(act));

    const matchesGrupo = filterGrupo === "" || entrevista.grupo === Number(filterGrupo);

    return matchesActividades && matchesGrupo;
  });

  const handleResetFilters = () => {
    setFilterActividades([]);
    setFilterGrupo("");
  };

  const handleActividadChange = (event) => {
    const value = event.target.value;
    setFilterActividades(typeof value === "string" ? value.split(",") : value);
  };

  const handleRowClick = (params) => {
    navigate(`/ver-entrevista/${params.row.id}/${params.row.pacienteId}`);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      { field: "pacienteId", headerName: "ID", width: 120 },
      { field: "paciente", headerName: "Paciente", width: 200, flex: 1 },
      {
        field: "fecha", headerName: "Fecha Entrevista", width: 160,
        sortComparator: (v1, v2, cp1, cp2) =>
          new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
      },
      { field: "tipo", headerName: "Tipo", width: 130 },
      { field: "grupoNombre", headerName: "Grupo", width: 180 },
      {
        field: "actividades",
        headerName: "Actividades",
        width: 300,
        flex: 1,
        renderCell: (params) => params.value.join(", "),
      },
    ];

    if (isAdmin) {
      baseColumns.push({
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ExcludeEntrevistaButton
            entrevistaId={params.row.id}
            onDeleted={fetchEntrevistas}
          />
        ),
      });
    }

    return baseColumns;
  }, [isAdmin, fetchEntrevistas]);

  return (
    <>
      <Typography variant="h6" gutterBottom color="#0D6AEE">
        Lista de Entrevistas
      </Typography>

      {/* Filtros */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        {/* Filtro por Actividades */}
        <Grid item xs>
          <Select
            size="small"
            fullWidth
            multiple
            value={filterActividades}
            onChange={handleActividadChange}
            renderValue={(selected) => (selected.length ? selected.join(", ") : "Selecciona actividades")}
            displayEmpty
          >
            <MenuItem disabled value="">
              Selecciona actividades
            </MenuItem>
            {actividadesDisponibles.map((actividad) => (
              <MenuItem key={actividad} value={actividad}>
                <Checkbox checked={filterActividades.includes(actividad)} />
                <ListItemText primary={actividad} />
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Filtro por Grupo */}
        <Grid item xs>
          <Select size="small" fullWidth value={filterGrupo} onChange={(e) => setFilterGrupo(e.target.value)} displayEmpty>
            <MenuItem value="">Todos los grupos</MenuItem>
            {Object.entries(GRUPOS_DICT).map(([key, name]) => (
              <MenuItem key={key} value={key}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item>
          <ToggleButtonGroup
            value={currentSort}
            exclusive
            size="small"
            onChange={(_, val) => {
              const opt = SORT_OPTIONS.find(o => o.value === val);
              if (opt) setSortModel(opt.model);
            }}
          >
            {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs>
          <Button fullWidth variant="contained" color="secondary" onClick={handleResetFilters} startIcon={<RestartAltIcon />}>
            Resetear
          </Button>
        </Grid>
      </Grid>

      {/* Data Grid con Toolbar */}
      {loading ? (
        <LinearProgress />
      ) : (
        <DataGrid
          rows={filteredEntrevistas}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50]}
          slots={{ toolbar: GridToolbar }}
          onRowClick={handleRowClick}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
        />
      )}
    </>
  );
}

export default EntrevistasTable;
